import React, { memo, useMemo } from "react";
import { Tab, Grid } from "semantic-ui-react";
import { MoreDataProps } from "../types";
import SimpleTable from "../../../components/shared/SimpleTable";
import QuickView from "../../../components/shared/quickview/QuickView";
import Output from "../../../openstack/orchestration/detailedview/Output";
import useStopPropagation from "../../../custom-hooks/useStopPropagation";
import useHasCRUDAccess from "../../../custom-hooks/useHasCRUDAccess";

const formatKey = (key: string): string =>
  key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const distributeData = (data: { [key: string]: any }, columns: number) => {
  const entries = Object.entries(data);
  const rowsPerColumn = Math.ceil(entries.length / columns);
  return Array.from({ length: columns }, (_, columnIndex) =>
    entries.slice(
      columnIndex * rowsPerColumn,
      (columnIndex + 1) * rowsPerColumn,
    ),
  );
};

const DetailsTab: React.FC<{ data: { [key: string]: any } }> = ({ data }) => {
  const distributedData = useMemo(() => {
    const columns = Math.min(3, Math.ceil(Object.keys(data).length / 3));
    return distributeData(data, columns);
  }, [data]);

  return (
    <Tab.Pane>
      <Grid className="columns-2">
        <Grid.Row stackable="true">
          {distributedData.map((columnData, columnIndex) => {
            const filteredRows = columnData.filter(
              ([, value]) => value !== "********",
            );
            const content = filteredRows.map(([key, value], rowIndex) => [
              formatKey(key),
              <QuickView key={rowIndex}>
                <QuickView.Trigger>{value}</QuickView.Trigger>
                <QuickView.Content>
                  <QuickView.Copy copy={value}>Copy</QuickView.Copy>
                </QuickView.Content>
              </QuickView>,
            ]);

            return (
              <Grid.Column key={columnIndex}>
                <SimpleTable className="striped-table" content={content} />
              </Grid.Column>
            );
          })}
        </Grid.Row>
      </Grid>
    </Tab.Pane>
  );
};

const StackOutputTab: React.FC<{ stack: any }> = ({ stack }) => {
  const hasCRUDAccess = useHasCRUDAccess("openstack");

  return (
    <Grid.Row stackable="true">
      <Grid.Column width={12}>
        <Output stack={stack} hasCRUDAccess={hasCRUDAccess} />
      </Grid.Column>
    </Grid.Row>
  );
};

const MoreData: React.FC<MoreDataProps> = ({ resource, stack }) => {
  const onStopPropagation = useStopPropagation();

  const panes = useMemo(() => {
    const tabs = [
      {
        menuItem: "Details",
        render: () => (
          <DetailsTab data={resource.data as { [key: string]: any }} />
        ),
      },
    ];

    if (resource.deploymentTypeTitle === "Heat" && stack) {
      tabs.push({
        menuItem: "Stack Output",
        render: () => <StackOutputTab stack={stack} />,
      });
    }

    return tabs;
  }, [resource, stack]);

  return (
    <div onClick={onStopPropagation} className="more-data more-data--tabs">
      <Tab
        defaultActiveIndex={0}
        className="horizontal-tabs padding-top-20 margin-bottom scrollable-menu"
        panes={panes}
      />
    </div>
  );
};

export default memo(MoreData);
